import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ApiError } from '../api-client';
import { WorqlyMark } from '../../design-system/components/WorqlyMark';
import { appUrl, getHostMode } from '../hostMode';
import { TurnstileWidget } from '../../marketing/components/TurnstileWidget';
import { MfaChallengeStep } from './MfaChallengeStep';

export function SetPasswordPage() {
  const { redeemMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [widgetEpoch, setWidgetEpoch] = React.useState(0);
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);

  function goDashboard() {
    if (getHostMode() === 'marketing') {
      window.location.replace(appUrl('/dashboard'));
    } else {
      navigate('/dashboard', { replace: true });
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!token) { setError('Magic link is missing. Use the link from your email.'); return; }

    setSubmitting(true);
    try {
      const result = await redeemMagicLink({ token, password, turnstileToken });
      if ('mfa_required' in result) {
        setMfaToken(result.mfa_token);
        return;
      }
      goDashboard();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'captcha_failed') {
          setError(`Captcha: ${err.message || 'verification failed'} — please try again.`);
          setTurnstileToken('');
          setWidgetEpoch((n) => n + 1);
        } else if (err.code === 'rate_limited') {
          setError('Too many attempts on this link — please wait a few minutes.');
        } else if (err.code === 'invalid_magic_link') {
          // "Magic link is invalid or expired" on its own is a dead end: it
          // does not say the link is single-use, and it does not say that for
          // an ADMIN who has never set a password, "Forgot password" sends
          // nothing (admin resets are restricted to an allowlist). Give the
          // actual next step instead.
          setError(
            'This link is no longer valid — it has either expired or already been used. '
            + 'Ask whoever invited you to send a new invite. If this is a Worqly admin account '
            + 'that has never had a password set, "Forgot password" will not work for it.',
          );
        } else {
          setError(err.message);
        }
      } else {
        setError(String((err as any)?.message ?? err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen flex flex-col">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-95" />

      <header className="relative z-10 px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="group" aria-label="Worqly home">
          <WorqlyMark size="md" />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/65 hover:text-ink dark:text-white/70 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to worqly.io
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Finish setup</p>
            <h1 className="font-light text-display-md text-ink dark:text-white tracking-[-0.025em] leading-[1.05]">Set your password.</h1>
            <p className="mt-3 text-[14px] text-ink/60 dark:text-white/60">You'll be signed in automatically after this.</p>
          </div>

          <div className="rounded-3xl bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card p-7">
            {mfaToken ? (
              <MfaChallengeStep
                mfaToken={mfaToken}
                onBack={() => { setMfaToken(null); setError(null); }}
                onSuccess={() => goDashboard()}
              />
            ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  autoFocus
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  placeholder="Type it again"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              <TurnstileWidget
                key={widgetEpoch}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
              />
              {error && (
                <div className="text-[13px] text-destructive bg-destructive/8 ring-1 ring-destructive/20 rounded-lg px-3 py-2" role="alert">
                  {error}
                </div>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={submitting || !turnstileToken}>
                {submitting ? 'Setting password…' : 'Set password & sign in'}
              </Button>
            </form>
            )}
          </div>

          <p className="text-center text-[13px] text-ink/60 dark:text-white/60 mt-8">
            Already have a password?{' '}
            <Link
              to="/login"
              className="inline-flex items-center gap-1 font-bold text-ink dark:text-white hover:text-coral dark:hover:text-coral transition-colors"
            >
              Sign in instead
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
